import { IEventAggregationService, ISubscriber } from '@studyportals/event-aggregation-service-interface';
import {
	AnonymousStudentProfileSynced,
	AnonymousStudentProfileUpdated,
	AnonymousStudentServiceReady,
	IAnonymousStudentService,
	StudentRepositoryStateType
} from '@studyportals/anonymous-student-interfaces';
import { DataStorage } from '@studyportals/data-storage';
import { StudentField } from '@studyportals/studentdomain';
import { router } from '../../router';
import { ApplicationStatusOptions } from '../../models/application-status/ApplicationStatusOptions';
import store from '..';
import { IStore } from '../../interfaces';

export class LocalizedContentPlugin
implements ISubscriber<AnonymousStudentServiceReady | AnonymousStudentProfileUpdated | AnonymousStudentProfileSynced>
{
	private anonymousStudent?: IAnonymousStudentService;
	private store: IStore;

	public constructor() {
		this.store = store;
	}

	public initiate(): void {
		const eventAggregationService = this.getEventAggregationService();
		if (eventAggregationService === undefined) {
			void this.setApplicationReadyAndStartSearch();
			return;
		}

		window.addEventListener('storage', (event) => {
			void (async (): Promise<void> => {
				if (event.key === 'global_access_initiative_questionnaire_taken') {
					const studentData = await this.anonymousStudent!.getStudentData([StudentField.RESIDENCE_COUNTRY_ID]);
					const countryOfResidenceId = studentData[StudentField.RESIDENCE_COUNTRY_ID];

					if (countryOfResidenceId) {
						this.store.mutations.storeUserCountryOfResidence(countryOfResidenceId);
						this.store.mutations.setGAIQuestionnaireTaken(DataStorage.retrieve(event.key) === 'true');
					}
				}
			})();
		});

		eventAggregationService.subscribeTo(AnonymousStudentServiceReady.EventType, this, true);
		eventAggregationService.subscribeTo(AnonymousStudentProfileUpdated.EventType, this, false);
		eventAggregationService.subscribeTo(AnonymousStudentProfileSynced.EventType, this, false);
	}

	public async notify(
		event: AnonymousStudentServiceReady | AnonymousStudentProfileUpdated | AnonymousStudentProfileSynced
	): Promise<void> {
		if (event.eventType === AnonymousStudentServiceReady.EventType) {
			await this.handleAnonymousStudentReadyEvent(event as AnonymousStudentServiceReady);
		}

		if (event.eventType === AnonymousStudentProfileUpdated.EventType) {
			await this.handleAnonymousStudentProfileUpdateEvent(event as AnonymousStudentProfileUpdated);
		}

		if (event.eventType === AnonymousStudentProfileSynced.EventType) {
			await this.handleAnonymousStudentProfileSynced(event as AnonymousStudentProfileSynced);
		}
	}

	private getEventAggregationService(): IEventAggregationService | undefined {
		return window.EventAggregationService;
	}

	private async handleAnonymousStudentProfileUpdateEvent(event: AnonymousStudentProfileUpdated): Promise<void> {
		await this.processUserNationalityAndCurrencyChange(event);
		this.processUserResidenceChange(event);
	}

	private async handleAnonymousStudentReadyEvent(event: AnonymousStudentServiceReady): Promise<void> {
		try {
			this.anonymousStudent = event.anonymousStudentService;
			const data = await this.anonymousStudent.getStudentData([
				StudentField.NATIONALITY_COUNTRY_ID,
				StudentField.CURRENCY,
				StudentField.RESIDENCE_COUNTRY_ID
			]);

			const nationalityCountryId = data[StudentField.NATIONALITY_COUNTRY_ID];
			if (nationalityCountryId) {
				this.store.mutations.storeUserNationality(nationalityCountryId.toString());
			}

			const residenceCountryId = data[StudentField.RESIDENCE_COUNTRY_ID];
			if (residenceCountryId) {
				this.store.mutations.storeUserCountryOfResidence(residenceCountryId);
			}

			const currency = data[StudentField.CURRENCY];
			if (currency) {
				this.store.mutations.storeUserCurrency(currency);
			}
		} finally {
			await this.setApplicationReadyAndStartSearch();
		}
	}

	private async handleAnonymousStudentProfileSynced(event: AnonymousStudentProfileSynced): Promise<void> {
		if (!this.anonymousStudent || event.state !== StudentRepositoryStateType.ONLINE) {
			return;
		}

		const data = await this.anonymousStudent.getStudentData([
			StudentField.NATIONALITY_COUNTRY_ID,
			StudentField.CURRENCY
		]);
		const nationalityId = data[StudentField.NATIONALITY_COUNTRY_ID]
			? data[StudentField.NATIONALITY_COUNTRY_ID].toString()
			: undefined;

		await this.store.actions.processUserCurrencyAndCountryChange(nationalityId, data[StudentField.CURRENCY]);
	}

	private async setApplicationReadyAndStartSearch(): Promise<void> {
		this.store.mutations.storeApplicationStatus(ApplicationStatusOptions.INITIALIZED);

		const currentRoute = router.currentRoute.value;
		const navigationFailed = currentRoute.matched.length === 0;
		if (navigationFailed) {
			return;
		}

		await this.store.actions.updateSearchResultsFromRoute(currentRoute);
	}

	private async processUserNationalityAndCurrencyChange(event: AnonymousStudentProfileUpdated): Promise<void> {
		const changes = event.changes;
		if (!changes[StudentField.NATIONALITY_COUNTRY_ID] && !changes[StudentField.CURRENCY]) {
			return;
		}

		const nationalityCountryId = changes[StudentField.NATIONALITY_COUNTRY_ID]
			? changes[StudentField.NATIONALITY_COUNTRY_ID].toString()
			: undefined;

		await this.store.actions.processUserCurrencyAndCountryChange(nationalityCountryId, changes[StudentField.CURRENCY]);
	}

	private processUserResidenceChange(event: AnonymousStudentProfileUpdated): void {
		const changes = event.changes;

		if (!changes[StudentField.RESIDENCE_COUNTRY_ID]) {
			return;
		}

		const residenceCountryId = changes[StudentField.RESIDENCE_COUNTRY_ID];
		this.store.mutations.storeUserCountryOfResidence(residenceCountryId);
	}
}
